import { forwardRef, useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronUp, Eye, EyeOff, Search, X } from 'lucide-react'
import { lengthError } from '../../lib/fieldLength'

/**
 * Inputs live on the white canvas — white surface, dark ink, light rule
 * border, cyan accent on focus. Radius rounded-lg per refreshed scale.
 *
 * Typography (TaskMagix standard — see Modal.jsx for the matching modal
 * tier):
 *   - Input value         text-sm (inherited)
 *   - Placeholder         text-sm at --color-ink-subtle
 *                         (was text-xs — sized-down placeholders looked
 *                          jarring next to text-sm input values)
 */
const inputBase =
  'w-full h-10 px-3 rounded-lg border bg-[color:var(--color-card)] ' +
  'text-[color:var(--color-ink)] text-sm ' +
  'placeholder:text-[color:var(--color-ink-subtle)] placeholder:text-sm ' +
  'outline-none transition-colors focus:border-[color:var(--color-accent)]'

/**
 * Field label — `text-sm font-medium`. Previously `text-base
 * font-semibold tracking-wide`, which shouted next to the input value
 * (same size+heavier+spaced) and visually competed with section
 * headings. Medium weight at 14px is loud enough to anchor a field
 * without dominating the form.
 */
export const FieldLabel = ({ label, required, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    // `w-fit` matters: a bare `block` label stretched to the FULL width of
    // the field column (e.g. 770px on My Profile) while its text is ~75px,
    // so ~90% of the label row was invisible-but-clickable. Clicking that
    // empty space activated the label and focused the control beneath —
    // the field lit up and started accepting input even though the click
    // was clearly outside the textbox (QA). Shrinking the box to its text
    // keeps the intended label-click-focuses-input affordance (and the
    // `htmlFor` association screen readers need) while making the hit
    // target match what the user can actually see.
    className="block w-fit mb-1.5 text-[14px] font-medium text-[color:var(--color-ink-strong)]"
  >
    {label}
    {required ? (
      <span
        aria-hidden="true"
        className="text-[color:var(--color-priority-urgent)]"
      >
        *
      </span>
    ) : null}
    {/* The visible "*" is aria-hidden, and the word "required" is NOT
     *  repeated here as sr-only text. The controls now carry
     *  `aria-required`, which is the correct place for it: screen readers
     *  announce it as the field's STATE ("Full Name, edit text,
     *  required") rather than baking the word into the field's NAME.
     *  Keeping both would make them announce "required" twice. */}
  </label>
)

// `FieldError` was folded into `FieldFooter` below — every field now
// renders its message and counter on one shared row, so a standalone
// error element would just reintroduce the extra row it replaced.

/**
 * The single row that sits beneath a control: message on the LEFT (the
 * error wins over helper text, since only one is worth reading), and the
 * character counter on the RIGHT.
 *
 * These used to be two stacked rows — counter first, then the error
 * underneath it. On a field with `showCount` that pushed the validation
 * message a whole row further down, so it read as a gap between the input
 * and its own message, and visually detached the two. Sharing one row
 * keeps the message tight under the field and puts the counter where the
 * eye already expects it.
 */
const FieldFooter = ({ helper, maxLength, value, showCount, error, errorId }) => {
  const len = (value ?? '').length
  // Fields are soft-limited (typing continues past the cap), so the counter
  // stays visible — turning red once OVER the limit — to show how far over the
  // value is, alongside the at-most-N error.
  const overMax = Boolean(maxLength && len > maxLength)
  const showCounter = Boolean(showCount && maxLength)
  if (!helper && !showCounter && !error) return null
  return (
    <div className="mt-1 flex items-start justify-between gap-3 text-xs">
      {error ? (
        // `id` matters: the control points at it via `aria-describedby`,
        // which previously referenced an element that had no id at all.
        <p
          id={errorId}
          role="alert"
          // `--color-danger-text`, not `--color-priority-urgent`: this message
          // renders at 12px, where #ef4444 on white is 3.76:1 and fails WCAG
          // 1.4.3 AA (defect D-A11Y-02). The urgent token still drives the
          // field BORDER below, which only needs 3:1 as a UI component.
          className="min-w-0 text-[color:var(--color-danger-text)]"
        >
          {error}
        </p>
      ) : (
        <span className="min-w-0 text-[color:var(--color-ink-muted)]">
          {helper}
        </span>
      )}
      {showCounter ? (
        <span
          className={
            'shrink-0 tabular-nums ' +
            (overMax
              ? 'text-[color:var(--color-danger-text)] font-semibold'
              : 'text-[color:var(--color-ink-muted)]')
          }
        >
          {len}/{maxLength}
        </span>
      ) : null}
    </div>
  )
}

/**
 * Up/down stepper for `type="number"` fields.
 *
 * Mobile browsers do NOT render the native `::-webkit-inner-spin-button`, so
 * a number field that had arrows on desktop had none on a phone — the user
 * could type a value but not nudge it (QA). Rendering our own buttons gives
 * both viewports the same control; the native spinner is hidden in index.css
 * so desktop never shows two sets.
 *
 * `stepUp`/`stepDown` are the browser's own implementations, so `min`, `max`
 * and `step` are honoured for free — including decimal steps like the
 * coupon Value field's `step="0.01"`, where hand-rolled arithmetic would
 * drift on floating point.
 *
 * React does not observe `stepUp()` (it mutates the DOM value directly), so
 * the new value is written through the native setter and an `input` event is
 * dispatched — the event React actually listens to. Without this a
 * controlled field would snap back on the next render.
 */
function NumberStepper({ inputRef, disabled }) {
  const nudge = (dir) => {
    const el = inputRef.current
    if (!el || el.disabled || el.readOnly) return
    const before = el.value
    try {
      if (dir > 0) el.stepUp()
      else el.stepDown()
    } catch {
      // `stepUp` throws when the current value cannot be stepped from (e.g.
      // a partially-typed entry). Fall back to the min, or zero.
      el.value = el.min !== '' ? el.min : '0'
    }
    if (el.value === before) return
    const setter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value',
    )?.set
    const next = el.value
    if (setter) {
      // Re-assign through the native setter so React's value tracker sees a
      // change; assigning `el.value` alone leaves the tracker in sync and
      // React swallows the event.
      el.value = before
      setter.call(el, next)
    }
    el.dispatchEvent(new Event('input', { bubbles: true }))
  }
  const btn =
    'flex h-1/2 w-7 items-center justify-center text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer'
  return (
    // `aria-hidden` + `tabIndex={-1}`: the input itself is already
    // keyboard-steppable with the arrow keys and is announced as a spin
    // button, so exposing these would add two redundant tab stops that read
    // as unlabelled buttons. They are a POINTER affordance only.
    <div
      aria-hidden="true"
      className="absolute right-px top-px bottom-px flex w-7 flex-col overflow-hidden rounded-r-lg border-l border-[color:var(--color-rule)]"
    >
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        onClick={() => nudge(1)}
        className={btn + ' border-b border-[color:var(--color-rule)]'}
      >
        <ChevronUp size={13} strokeWidth={2.5} />
      </button>
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        onClick={() => nudge(-1)}
        className={btn}
      >
        <ChevronDown size={13} strokeWidth={2.5} />
      </button>
    </div>
  )
}

export const TextField = forwardRef(function TextField(
  { label, name, required, helperText, error, minLength, maxLength, showCount = false, value, defaultValue, onChange, onBlur, className, autoTrim = true, ...rest },
  ref,
) {
  const id = rest.id ?? name
  // Local ref so the stepper can drive the input, merged with any forwarded
  // ref (react-hook-form passes one) so both keep working.
  const innerRef = useRef(null)
  const setRefs = (el) => {
    innerRef.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref && typeof ref === 'object') ref.current = el
  }
  const isNumber = rest.type === 'number'
  // Track the value internally so we can run live length-validation
  // regardless of whether the field is controlled (parent passes `value`)
  // or uncontrolled (react-hook-form's `register()` only wires up
  // onChange + ref, no value prop). Without this, RHF-wired inputs
  // never trigger lengthError because `value` stays undefined.
  const [internalValue, setInternalValue] = useState(
    value ?? defaultValue ?? '',
  )
  useEffect(() => {
    if (value !== undefined) setInternalValue(value)
  }, [value])
  // Uncontrolled (react-hook-form `register`) fields get their value written
  // straight into the DOM by `reset()` / `defaultValues` — no change event —
  // so the "n/50" counter and the live length check read 0 for a pre-filled
  // value (e.g. My Profile's Full Name). Pick the DOM value up after each
  // render; only sets state when it actually differs, so it settles at once.
  // Runs after every render on purpose (no deps): reset() writes the DOM
  // without an event. It only sets state when the value differs, so it settles.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (value !== undefined) return
    const domValue = innerRef.current?.value
    if (typeof domValue === 'string' && domValue !== internalValue) setInternalValue(domValue)
  })
  // Strip leading whitespace as the user types so a typed value can
  // never start with a space — the most common "I didn't mean that"
  // case (especially when the user paste-then-edits a phone number
  // / name / email). Trailing whitespace is handled on blur (below).
  // Submit-time clean-up is the last line of defence: every shared
  // Zod schema in `lib/validators.js` still runs `.trim()`.
  // Opt out by passing `autoTrim={false}` (e.g. search fields where
  // a trailing space could conceivably be intentional).
  const handleChange = (e) => {
    const raw = e.target.value
    const next = autoTrim && typeof raw === 'string' ? raw.replace(/^\s+/, '') : raw
    if (next !== raw) {
      // Mutate the synthetic event's currentTarget so the caller's
      // onChange (often RHF's) sees the cleaned value when it reads
      // `event.target.value`. Safe because the event is React-owned.
      e.target.value = next
    }
    setInternalValue(next)
    if (onChange) onChange(e)
  }
  // Trim trailing whitespace on blur. Doing it here (rather than in
  // onChange) lets the user freely type spaces mid-word; only the
  // dangling trailing space is reaped when they leave the field.
  const handleBlur = (e) => {
    if (autoTrim && typeof e.target.value === 'string') {
      const trimmed = e.target.value.replace(/\s+$/, '')
      if (trimmed !== e.target.value) {
        e.target.value = trimmed
        setInternalValue(trimmed)
        // Re-emit a synthetic change so RHF (or the controlled
        // parent) records the trimmed value, not the original.
        if (onChange) onChange(e)
      }
    }
    if (onBlur) onBlur(e)
  }
  const effectiveValue = value !== undefined ? value : internalValue
  // Caller-supplied error wins; if absent, fall back to the live length
  // check so users see "<Field> must be at least 2 characters" or
  // "<Field> must be at most N characters" immediately.
  const effectiveError = error ?? lengthError(effectiveValue, minLength, maxLength, label)
  const borderClass = effectiveError
    ? 'border-[color:var(--color-priority-urgent)]'
    : 'border-[color:var(--color-rule)]'
  return (
    <div>
      <FieldLabel label={label} required={required} htmlFor={id} />
      <div className="relative">
        <input
          ref={setRefs}
          id={id}
          name={name}
          // No DOM `maxLength`: the field SOFT-limits — it keeps accepting input
          // past the cap and surfaces `lengthError` instead of silently blocking
          // keystrokes. Persistence is guarded by the Zod schema + server DTO.
          minLength={minLength}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onBlur={handleBlur}
          aria-invalid={Boolean(effectiveError)}
          aria-required={required || undefined}
          aria-describedby={effectiveError ? `${id}-error` : undefined}
          data-fd-stepper={isNumber ? '' : undefined}
          className={`${inputBase} ${borderClass} ${isNumber ? 'pr-9' : ''} ${className ?? ''}`}
          {...rest}
        />
        {isNumber ? <NumberStepper inputRef={innerRef} disabled={rest.disabled} /> : null}
      </div>
      <FieldFooter
        helper={helperText}
        maxLength={maxLength}
        value={effectiveValue}
        showCount={showCount}
        error={effectiveError}
        errorId={`${id}-error`}
      />
    </div>
  )
})

/**
 * Multi-line counterpart of TextField. Same validation behaviour (min /
 * max length, live counter, below-field error) but renders a textarea.
 * Inherits the global `textarea { min-height; max-height }` resize cap.
 */
export const TextAreaField = forwardRef(function TextAreaField(
  { label, name, required, helperText, error, minLength, maxLength, showCount = false, value, defaultValue, onChange, onBlur, className, rows = 3, autoTrim = true, ...rest },
  ref,
) {
  const id = rest.id ?? name
  // Same internal-value tracking as TextField so RHF-wired textareas
  // also see live length validation.
  const [internalValue, setInternalValue] = useState(
    value ?? defaultValue ?? '',
  )
  useEffect(() => {
    if (value !== undefined) setInternalValue(value)
  }, [value])
  // Local ref merged with the forwarded one, so a value `reset()` writes into
  // the DOM is counted too — see the matching effect in `TextField`.
  const innerRef = useRef(null)
  const setRefs = (el) => {
    innerRef.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref && typeof ref === 'object') ref.current = el
  }
  // Runs after every render on purpose (no deps): reset() writes the DOM
  // without an event. It only sets state when the value differs, so it settles.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (value !== undefined) return
    const domValue = innerRef.current?.value
    if (typeof domValue === 'string' && domValue !== internalValue) setInternalValue(domValue)
  })
  // Strip leading whitespace as the user types; trailing whitespace
  // is reaped on blur. See `TextField` for the rationale.
  const handleChange = (e) => {
    const raw = e.target.value
    const next = autoTrim && typeof raw === 'string' ? raw.replace(/^\s+/, '') : raw
    if (next !== raw) {
      e.target.value = next
    }
    setInternalValue(next)
    if (onChange) onChange(e)
  }
  const handleBlur = (e) => {
    if (autoTrim && typeof e.target.value === 'string') {
      const trimmed = e.target.value.replace(/\s+$/, '')
      if (trimmed !== e.target.value) {
        e.target.value = trimmed
        setInternalValue(trimmed)
        if (onChange) onChange(e)
      }
    }
    if (onBlur) onBlur(e)
  }
  const effectiveValue = value !== undefined ? value : internalValue
  const effectiveError = error ?? lengthError(effectiveValue, minLength, maxLength, label)
  const borderClass = effectiveError
    ? 'border-[color:var(--color-priority-urgent)]'
    : 'border-[color:var(--color-rule)]'
  const textareaBase =
    'w-full px-3 py-2 rounded-lg border bg-[color:var(--color-card)] ' +
    'text-[color:var(--color-ink)] ' +
    'placeholder:text-[color:var(--color-ink-subtle)] placeholder:text-xs ' +
    'outline-none transition-colors focus:border-[color:var(--color-accent)] resize-y leading-relaxed'
  return (
    <div>
      <FieldLabel label={label} required={required} htmlFor={id} />
      <textarea
        ref={setRefs}
        id={id}
        name={name}
        // No DOM `maxLength` — soft limit (see TextField): keeps accepting input
        // past the cap and shows `lengthError`; the Zod schema + DTO block save.
        minLength={minLength}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        onBlur={handleBlur}
        rows={rows}
        aria-invalid={Boolean(effectiveError)}
        aria-required={required || undefined}
        aria-describedby={effectiveError ? `${id}-error` : undefined}
        className={`${textareaBase} ${borderClass} ${className ?? ''}`}
        {...rest}
      />
      <FieldFooter
        helper={helperText}
        maxLength={maxLength}
        value={effectiveValue}
        showCount={showCount}
        error={effectiveError}
        errorId={`${id}-error`}
      />
    </div>
  )
})

export const PasswordField = forwardRef(function PasswordField(
  { label, name, required, helperText, error, maxLength, value, className, ...rest },
  ref,
) {
  const [visible, setVisible] = useState(false)
  const id = rest.id ?? name
  const borderClass = error
    ? 'border-[color:var(--color-priority-urgent)]'
    : 'border-[color:var(--color-rule)]'
  return (
    <div>
      <FieldLabel label={label} required={required} htmlFor={id} />
      <div className="relative">
        <input
          ref={ref}
          id={id}
          name={name}
          type={visible ? 'text' : 'password'}
          maxLength={maxLength}
          value={value}
          aria-invalid={Boolean(error)}
          aria-required={required || undefined}
          // TextField already did this; PasswordField and DateField set
          // `aria-invalid` but never pointed at the message, so a screen
          // reader was told the field was invalid and never told why.
          aria-describedby={error ? `${id}-error` : undefined}
          className={`${inputBase} pr-10 ${borderClass} ${className ?? ''}`}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          title={visible ? 'Hide password' : 'Show password'}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          {visible ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>
      </div>
      <FieldFooter helper={helperText} error={error} errorId={`${id}-error`} />
    </div>
  )
})

export const DateField = forwardRef(function DateField(
  { label, name, required, helperText, error, className, ...rest },
  ref,
) {
  const id = rest.id ?? name
  const borderClass = error
    ? 'border-[color:var(--color-priority-urgent)]'
    : 'border-[color:var(--color-rule)]'
  return (
    <div>
      <FieldLabel label={label} required={required} htmlFor={id} />
      {/* No custom calendar icon here — the browser's native date-picker
       *  indicator is already styled app-wide in index.css. Adding a lucide
       *  icon on top produced a DOUBLE calendar icon in Chromium. */}
      <input
        ref={ref}
        id={id}
        name={name}
        type="date"
        aria-invalid={Boolean(error)}
        aria-required={required || undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        placeholder="DD/MM/YYYY"
        className={`${inputBase} ${borderClass} ${className ?? ''}`}
        {...rest}
      />
      <FieldFooter helper={helperText} error={error} errorId={`${id}-error`} />
    </div>
  )
})

export const SearchField = forwardRef(function SearchField(
  { onClear, value, className, placeholder, ...rest },
  ref,
) {
  const hasValue = (value ?? '').length > 0
  // Leading magnifier INSIDE the pill (left), a clear "✕" inside on the right
  // when there's a value — the same treatment FilterRow and the rest of the
  // app's search bars use, so every search field reads as one visual family.
  return (
    <div className="relative w-full">
      {/* `z-10` keeps the icon above the pill's backdrop-filter, which would
       *  otherwise render over an un-layered icon and hide it. */}
      <span
        aria-hidden="true"
        className="absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[color:var(--color-ink-muted)] pointer-events-none"
      >
        <Search size={15} />
      </span>
      <input
        ref={ref}
        // Use type="text" with role="searchbox" — type="search" renders a
        // native clear glyph in some browsers that fights our custom X.
        type="text"
        role="searchbox"
        value={value}
        placeholder={placeholder ?? 'Search'}
        // Rounded pill + sidebar-navy hairline border + navy focus glow.
        // Deliberately NOT `inputBase`; a self-contained class string avoids
        // Tailwind conflicts. `pl-9` leaves room for the leading icon.
        className={`fd-search-pill w-full h-10 pl-9 pr-9 rounded-full text-[color:var(--color-ink)] text-sm placeholder:text-[color:var(--color-ink-subtle)] outline-none ${className ?? ''}`}
        {...rest}
      />
      {hasValue ? (
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear search"
          title="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          <X size={14} />
        </button>
      ) : null}
    </div>
  )
})
